import React from "react";
import { MapPin, Navigation, Clock, Phone } from "lucide-react";

const MAP_LINK =
  "https://www.google.com/maps/search/?api=1&query=3HXF%2BV2+Bengaluru+Karnataka";
// Embed uses the exact plus code so only the clinic pin is shown
const MAP_EMBED =
  "https://www.google.com/maps?q=3HXF%2BV2+Yelahanka+New+Town+Bengaluru&z=17&output=embed";

const ADDRESS_LINES = [
  "MANASA — 61'B, SFS 208 Colony,",
  "Yelahanka New Town,",
  "Bengaluru, Karnataka 560064",
];
const PLUS_CODE = "3HXF+V2 Bengaluru, Karnataka";

const Location = () => {
  return (
    <section id="location" className="relative bg-[#fbf9f4] py-24 md:py-32">
      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#c4a257]" />
            <span className="text-[#c4a257] text-xs tracking-[0.3em] uppercase font-medium">
              Find Us
            </span>
            <span className="h-px w-10 bg-[#c4a257]" />
          </div>
          <h2 className="font-serif text-[38px] md:text-[54px] leading-tight text-[#1b5e3a]">
            Visit Our <span className="italic text-[#2c7a4a]">Sanctuary</span>
          </h2>
          <p className="text-[#5a6a5d] mt-5 max-w-2xl mx-auto">
            Nestled in Bengaluru, our center is a calm, natural space designed
            to help you unwind, heal and rediscover balance.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-stretch">
          {/* Map */}
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden border border-[#efe7d5] shadow-[0_20px_60px_rgba(27,94,58,0.12)] bg-white">
            <div className="relative w-full h-[420px] md:h-[520px]">
              <iframe
                title="Tatva Wellness Center location"
                src={MAP_EMBED}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          {/* Info Panel */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="bg-white border border-[#efe7d5] rounded-2xl p-7 shadow-[0_16px_40px_rgba(27,94,58,0.08)]">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-12 h-12 rounded-full bg-[#eaf1e6] flex items-center justify-center text-[#1b5e3a]">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    Address
                  </p>
                  <p className="font-serif text-lg text-[#1b5e3a] mt-1 leading-snug">
                    Tatva Wellness Center
                  </p>
                  <p className="text-sm text-[#5a6a5d] mt-1 leading-relaxed">
                    {ADDRESS_LINES.map((l) => (
                      <span key={l} className="block">
                        {l}
                      </span>
                    ))}
                  </p>
                  <p className="mt-2 inline-block text-[11px] uppercase tracking-[0.2em] text-[#c4a257] font-medium">
                    Plus Code · {PLUS_CODE}
                  </p>
                </div>
              </div>

              <a
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 group inline-flex w-full items-center justify-center gap-2 bg-[#1b5e3a] hover:bg-[#164d30] text-white text-sm font-semibold tracking-[0.12em] uppercase px-6 py-3.5 rounded-md shadow-[0_10px_24px_rgba(27,94,58,0.28)] transition-all duration-300 hover:-translate-y-0.5"
              >
                <Navigation className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                Get Directions
              </a>
            </div>

            <div className="bg-white border border-[#efe7d5] rounded-2xl p-7 shadow-[0_16px_40px_rgba(27,94,58,0.08)]">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-12 h-12 rounded-full bg-[#eaf1e6] flex items-center justify-center text-[#1b5e3a]">
                  <Clock className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#6b7568]">
                    Opening Hours
                  </p>
                  <p className="font-serif text-lg text-[#1b5e3a] mt-1">
                    Monday – Saturday
                  </p>
                  <p className="text-sm text-[#5a6a5d] mt-0.5">
                    8:00 AM – 7:00 PM
                  </p>
                  <p className="text-xs text-[#c4a257] uppercase tracking-[0.2em] mt-3">
                    Sunday · By Appointment
                  </p>
                </div>
              </div>
            </div>

            <a
              href="tel:+919845342025"
              className="group bg-gradient-to-br from-[#1b5e3a] to-[#0f2a1c] text-white rounded-2xl p-7 flex items-center gap-4 shadow-[0_16px_40px_rgba(27,94,58,0.2)] hover:shadow-[0_20px_50px_rgba(27,94,58,0.32)] transition-all duration-300 hover:-translate-y-0.5"
            >
              <span className="shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#c4a257]">
                  Prefer a Call?
                </p>
                <p className="font-serif text-lg mt-0.5">+91 98453 42025</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
